import { ConsultationDetails, AgoraInfo } from "../libs/types";
import { safeStorage } from "./safeStorage";

type SessionState = {
  accessTokens: Record<string, string>;
  consultationInfo: ConsultationDetails | null;
  agoraInfo: Record<string, AgoraInfo>;
};

const state: SessionState = {
  accessTokens: {},
  consultationInfo: null,
  agoraInfo: {},
};

const parseJSON = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const sessionStore = {
  hydrate(consultationId?: string) {
    if (consultationId && !state.accessTokens[consultationId]) {
      const stored = safeStorage.getSessionItem(
        `access_token_${consultationId}`,
      );
      if (stored) state.accessTokens[consultationId] = stored;
    }
    if (!state.consultationInfo) {
      state.consultationInfo = parseJSON<ConsultationDetails>(
        safeStorage.getSessionItem("consultationInfo"),
      );
    }
    if (consultationId && !state.agoraInfo[consultationId]) {
      const stored = parseJSON<AgoraInfo>(
        safeStorage.getSessionItem(`agoraInfo_${consultationId}`),
      );
      if (stored) state.agoraInfo[consultationId] = stored;
    }
  },
  getAccessToken(consultationId: string) {
    this.hydrate(consultationId);
    return state.accessTokens[consultationId] ?? null;
  },
  setAccessToken(consultationId: string, token: string | null) {
    if (!consultationId) return;
    if (token) {
      state.accessTokens[consultationId] = token;
      safeStorage.setSessionItem(`access_token_${consultationId}`, token);
    } else {
      delete state.accessTokens[consultationId];
      safeStorage.removeSessionItem(`access_token_${consultationId}`);
    }
  },
  getConsultationInfo() {
    this.hydrate();
    return state.consultationInfo;
  },
  setConsultationInfo(info: ConsultationDetails | null) {
    state.consultationInfo = info;
    if (info) {
      safeStorage.setSessionItem("consultationInfo", JSON.stringify(info));
    } else {
      safeStorage.removeSessionItem("consultationInfo");
    }
  },
  getAgoraInfo(consultationId: string) {
    this.hydrate(consultationId);
    return state.agoraInfo[consultationId] ?? null;
  },
  setAgoraInfo(consultationId: string, info: AgoraInfo | null) {
    if (!consultationId) return;
    if (info) {
      state.agoraInfo[consultationId] = info;
      safeStorage.setSessionItem(
        `agoraInfo_${consultationId}`,
        JSON.stringify(info),
      );
    } else {
      delete state.agoraInfo[consultationId];
      safeStorage.removeSessionItem(`agoraInfo_${consultationId}`);
    }
  },
  // For callers with no consultation id to hand (e.g. error-boundary
  // resets) that need every consultation's stored token gone.
  clearAllAgoraInfo() {
    Object.keys(state.agoraInfo).forEach((consultationId) =>
      this.setAgoraInfo(consultationId, null),
    );
    try {
      Object.keys(window.sessionStorage)
        .filter((key) => key.startsWith("agoraInfo_"))
        .forEach((key) => safeStorage.removeSessionItem(key));
    } catch {
      // Storage unavailable — nothing was stored to clear.
    }
  },
  clearAuth(consultationId?: string) {
    if (consultationId) {
      delete state.accessTokens[consultationId];
      safeStorage.removeSessionItem(`access_token_${consultationId}`);
    }
  },
};
